import { useState } from 'react';
import { CardGrid } from '../../components/CardGrid';
import { ContentArea } from '../../components/ContentArea';
import { EmptyMessage } from '../../components/EmptyMessage';
import { FilterPill, FilterPillGroup } from '../../components/FilterPill';
import { Section, Title, TitleRow } from '../../components/Section';
import { SkeletonCard } from '../../components/SkeletonCard';
import { SpaDayCard } from '../../composites/SpaDayCard';
import type { SpaDayListTemplateProps } from './SpaDayListTemplate.types';

const ALL_DURATIONS = '__all__';

export function SpaDayListTemplate({
  spaDays,
  onSelectSpaDay,
  isLoading,
  durationOptions,
  titleLabel = 'Spa days',
  fromPriceLabel = 'from',
  allDurationsLabel = 'All Duration',
  soldOutLabel = 'Sold out',
  noResultsLabel = 'No spa days available.',
  noImageLabel = 'No image',
  formatGuests,
}: SpaDayListTemplateProps) {
  const [selectedDuration, setSelectedDuration] = useState<string>(ALL_DURATIONS);

  const filteredSpaDays =
    selectedDuration === ALL_DURATIONS
      ? spaDays
      : spaDays.filter((sd) => sd.durationValue === selectedDuration);

  const durationFilters = durationOptions && durationOptions.length > 0 && (
    <FilterPillGroup>
      <FilterPill
        type="button"
        $active={selectedDuration === ALL_DURATIONS}
        onClick={() => setSelectedDuration(ALL_DURATIONS)}
      >
        {allDurationsLabel}
      </FilterPill>
      {durationOptions.map((option) => (
        <FilterPill
          key={option.value}
          type="button"
          $active={selectedDuration === option.value}
          onClick={() => setSelectedDuration(option.value)}
        >
          {option.label}
        </FilterPill>
      ))}
    </FilterPillGroup>
  );

  if (isLoading && spaDays.length === 0) {
    return (
      <Section>
        <TitleRow>
          <Title>{titleLabel}</Title>
        </TitleRow>
        <CardGrid>
          {Array.from({ length: 6 }).map((_, index) => (
            <SkeletonCard key={index} variant="grid" />
          ))}
        </CardGrid>
      </Section>
    );
  }

  if (!isLoading && spaDays.length === 0) {
    return (
      <Section>
        <TitleRow>
          <Title>{titleLabel}</Title>
        </TitleRow>
        <EmptyMessage>{noResultsLabel}</EmptyMessage>
      </Section>
    );
  }

  return (
    <Section>
      <TitleRow>
        <Title>{titleLabel}</Title>
        {durationFilters}
      </TitleRow>

      <ContentArea>
        {filteredSpaDays.length === 0 ? (
          <EmptyMessage>{noResultsLabel}</EmptyMessage>
        ) : (
          <CardGrid>
            {filteredSpaDays.map((spaDay) => (
              <SpaDayCard
                key={spaDay.id}
                id={spaDay.id}
                name={spaDay.name}
                imageUrl={spaDay.imageUrl}
                price={spaDay.price}
                discount={spaDay.discount}
                duration={spaDay.duration}
                numberOfGuests={spaDay.numberOfGuests}
                unavailable={spaDay.unavailable}
                fromPriceLabel={fromPriceLabel}
                soldOutLabel={soldOutLabel}
                noImageLabel={noImageLabel}
                formatGuests={formatGuests}
                onSelect={onSelectSpaDay}
              />
            ))}
          </CardGrid>
        )}
      </ContentArea>
    </Section>
  );
}
